import React from 'react';
import { Section as SectionDI, SkeletonItem as SkeletonItemDI } from '@atlaskit/side-navigation';
import { useIntlV2 as useIntlDI } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { MenuItem as MenuItemDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/common/ui/menu-item/main.tsx';
import { useBoard as useBoardDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/board/index.tsx';
import { useProject as useProjectDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { ScopeSwitcher as ScopeSwitcherDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/ui/scope-switcher/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { matchUrlPathname as matchUrlPathnameDI } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/url-matchers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlexibleNomenclature as useFlexibleNomenclatureDI } from '../../../../controllers/flexible-nomenclature/index.tsx';
import { messages } from './messages.tsx';
import type { AgileReportsProps } from './types.tsx';

const useThrowProjectDI = withThrowOnError(useProjectDI);
const useThrowBoardDI = withThrowOnError(useBoardDI);
const useThrowFlexibleNomenclatureDI = withThrowOnError(useFlexibleNomenclatureDI);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default ({
	MenuItem = MenuItemDI,
	Section = SectionDI,
	SkeletonItem = SkeletonItemDI,
	ScopeSwitcher = ScopeSwitcherDI,
	useIntl = useIntlDI,
	useProject = useThrowProjectDI,
	useBoard = useThrowBoardDI,
	useFlexibleNomenclature = useThrowFlexibleNomenclatureDI,
	matchUrlPathname = matchUrlPathnameDI,
}: AgileReportsProps) => {
	const { formatMessage } = useIntl();
	const { data: board } = useBoard();
	const { data: project } = useProject();

	if (project == null) return <SkeletonItem />;
	if (board == null) return null;

	const { data: flexibleNomenclature } = useFlexibleNomenclature();
	const isScrumProject = board.type === 'scrum';
	const baseReportUrl = `/jira/software/c/projects/${project.key}/boards/${board.id}/reports`;
	const epicLevelName = flexibleNomenclature.levelOneName || 'Epic';

	return (
		<Section title={formatMessage(messages.agile)}>
			<ScopeSwitcher />
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'burndown' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/burndown-chart`}
				>
					{formatMessage(messages.burndown)}
				</MenuItem>
			)}
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'burnup' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/burnup-chart`}
				>
					{formatMessage(messages.burnup)}
				</MenuItem>
			)}
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'sprint-retrospective' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/sprint-retrospective`}
				>
					{formatMessage(messages.sprint)}
				</MenuItem>
			)}
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'velocity' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}${fg('reports-overview-page-migration') ? '/velocity' : '/velocity-chart'}`}
				>
					{formatMessage(messages.velocity)}
				</MenuItem>
			)}
			<MenuItem
				analytics={{ itemId: 'cumulative-flow-diagram' }}
				selectedOn={matchUrlPathname()}
				href={`${baseReportUrl}/cumulative-flow-diagram`}
			>
				{formatMessage(messages.flow)}
			</MenuItem>
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'version' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/version-report`}
				>
					{formatMessage(messages.version)}
				</MenuItem>
			)}
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'epic-report' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/epic-report`}
				>
					{epicLevelName !== 'Epic'
						? formatMessage(messages.epicReportFlexible, { epicLevelName })
						: formatMessage(messages.epic)}
				</MenuItem>
			)}
			<MenuItem
				analytics={{ itemId: 'control' }}
				selectedOn={matchUrlPathname()}
				href={`${baseReportUrl}/control-chart`}
			>
				{formatMessage(messages.control)}
			</MenuItem>
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'epic-burndown' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/epic-burndown`}
				>
					{epicLevelName !== 'Epic'
						? formatMessage(messages.epicBurndownFlexible, { epicLevelName })
						: formatMessage(messages.epicBurndown)}
				</MenuItem>
			)}
			{isScrumProject && (
				<MenuItem
					analytics={{ itemId: 'release-burndown' }}
					selectedOn={matchUrlPathname()}
					href={`${baseReportUrl}/release-burndown`}
				>
					{formatMessage(messages.releaseBurndown)}
				</MenuItem>
			)}
		</Section>
	);
};
